import React from 'react'
import { connect } from 'react-redux'
import dot from 'dot-prop-immutable'
import cloneDeep from 'lodash/cloneDeep'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import PropTypes from 'prop-types'
import { Loader } from '@manychat/manyui'

import * as appSelectors from 'common/core/selectors/appSelectors'
import Filters from 'common/filter/components/Filters'
import EnsureSegmentsController from 'common/filter/controllers/EnsureSegments'
import { handleFilterAnalyticsData } from 'common/filter/filterAnalytics'
import { filterFieldMapper } from 'common/filter/filterFieldMapper'
import { FilterScenario, FilterSource } from 'common/filter/interfaces'
import { parseFilter, processFilter } from 'common/filter/models/AudienceFilter/adapter'
import { UNARY_OPERATORS } from 'common/filter/models/AudienceFilter/constants'
import getDefaultFilter from 'common/filter/models/AudienceFilter/defaults'
import {
  hasConditions,
  createCondition,
  pushCondition,
  removeCondition,
} from 'common/filter/models/AudienceFilter/helpers'
import { getActiveReasons } from 'common/oneTimeNotify/selectors/notifyReasonSelectors'
import { analyticsService } from 'utils/services/analytics'

class FiltersController extends React.Component {
  static propTypes = {
    filter: PropTypes.object,
    onChange: PropTypes.func,
    defaultFilters: PropTypes.exact({
      operator: PropTypes.string,
      groups: PropTypes.arrayOf(
        PropTypes.shape({
          operator: PropTypes.string,
          items: PropTypes.arrayOf(PropTypes.object),
        }),
      ),
    }),
    allowDateFormula: PropTypes.bool,
    theme: PropTypes.object,
    fieldsSelector: PropTypes.func,
    scenario: PropTypes.oneOf(Object.values(FilterScenario)),
    operatorSelectorAlwaysVisible: PropTypes.bool,
    builderId: PropTypes.string,
    isConditionBuilder: PropTypes.bool,
    nodeId: PropTypes.string,
    disabled: PropTypes.bool,
    source: PropTypes.oneOf(Object.values(FilterSource)),
  }

  static defaultProps = {
    filter: getDefaultFilter(),
    onChange: () => {},
    allowDateFormula: true,
  }

  constructor(props, context) {
    super(props, context)
    this.state = {
      filter: this.fixFilter(props.filter, props.defaultFilters),
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.filter !== prevProps.filter) {
      const filter = this.fixFilter(this.props.filter, this.props.defaultFilters)
      this.setState({ filter })
    }
  }

  fixFilter = (filter, defaultFilters) => {
    const filterGroupWithoutEmpty =
      filter && filter.groups && cloneDeep(filter.groups.filter((g) => g.items.length))
    filterGroupWithoutEmpty?.forEach((group) => group?.items.map((item) => delete item._oid))
    const defaultFilterGroupWithoutEmpty =
      defaultFilters && cloneDeep(defaultFilters.groups.filter((g) => g.items.length))

    const isFilterEqualDefault =
      defaultFilterGroupWithoutEmpty &&
      isEqual(filterGroupWithoutEmpty, defaultFilterGroupWithoutEmpty)

    return parseFilter(
      !isEmpty(filter) && !isFilterEqualDefault ? filter : getDefaultFilter(defaultFilters),
    )
  }

  _emitChange(data, action, force) {
    this.setState({ filter: data })

    const filter = processFilter(hasConditions(data) ? data : null)

    if (this.props.source !== FilterSource.LIVECHAT && action !== 'delete') {
      handleFilterAnalyticsData(filter, this.props.source)
    }

    if (
      filter &&
      filter.groups
        .reduce((items, group) => items.concat(group.items), [])
        .filter((item) => item.is_formula).length
    ) {
      analyticsService.sendEvent('FILTER.CUF_FORMULA_CONDITION_USED')
    }

    if (action !== 'create' || force) {
      this.props.onChange(filter)
    }
  }

  handleOperatorChange = (operator, groupIndex) => {
    const { filter } = this.state

    const res = dot.set(filter, `groups.${groupIndex}.operator`, operator)
    this._emitChange(res, 'operator')
  }

  handleCreateCondition = (groupIndex, field) => {
    const { nodeId } = this.props
    const { filter } = this.state

    const condition = createCondition(field, null, null, { isBuilder: Boolean(nodeId) })
    const res = pushCondition(filter, groupIndex, condition)
    this._emitChange(res, 'create', UNARY_OPERATORS.includes(condition.operator))
  }

  handleChangeCondition = (groupIndex, _oid, data) => {
    const { filter } = this.state

    const index = filter.groups[groupIndex].items.findIndex((i) => i._oid === _oid)
    let res = dot.merge(filter, `groups.${groupIndex}.items.${index}`, data)
    const condition = res.groups[groupIndex].items[index]

    if (
      (condition.value === null || !`${condition.value}`) &&
      !UNARY_OPERATORS.includes(condition.operator)
    ) {
      res = removeCondition(res, groupIndex, index)
    }
    this._emitChange(res, 'update')
  }

  handleRemoveCondition = (groupIndex, _oid) => {
    const { filter } = this.state

    const index = filter.groups[groupIndex].items.findIndex((i) => i._oid === _oid)
    const res = removeCondition(filter, groupIndex, index)

    this._emitChange(res, 'delete')
  }

  render() {
    const { operator, groups } = this.state.filter
    const { botTimezone } = this.props

    return (
      <EnsureSegmentsController loader={<Loader />}>
        <Filters
          {...this.props}
          operator={operator}
          groups={groups}
          onOperatorChange={this.handleOperatorChange}
          onCreateCondition={this.handleCreateCondition}
          onChangeCondition={this.handleChangeCondition}
          onRemoveCondition={this.handleRemoveCondition}
          botTimezone={botTimezone}
          source={this.props.source}
        />
      </EnsureSegmentsController>
    )
  }
}

export default connect((state, props) => {
  return {
    fields: props.fieldsSelector(state, props.builderId).map(filterFieldMapper),
    segments: state.segments.byId,
    activeReasons: getActiveReasons(state),
    botTimezone: appSelectors.getBotTimezone(state),
  }
})(FiltersController)
